import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type {
  CrawlJob,
  CrawlSchedule,
  WithResourceId,
} from '../../../types/knowledge';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';
import {
  useDeleteKnowledgeCrawlMutation,
  useDeleteKnowledgeCrawlScheduleMutation,
  useRestartKnowledgeCrawlMutation,
  useResumeAbandonedKnowledgeCrawlMutation,
  useRetryFailedKnowledgeCrawlMutation,
} from '../mutations/useKnowledgeCrawlMutations';
import { useKnowledgeDatabaseSharedStore } from '../stores/useKnowledgeDatabaseSharedStore';

type UseCrawlListActionsOptions = {
  databaseId: string;
  crawlsQuery: UseQueryResult<Array<WithResourceId<CrawlJob>>>;
  schedulesQuery: UseQueryResult<Array<WithResourceId<CrawlSchedule>>>;
  crawlScheduleByJobId: Map<string, WithResourceId<CrawlSchedule>>;
  clearLiveStatusForCrawl: (crawlId: string) => void;
  restartCrawlStatusStreamIfStopped: (crawlId: string) => void;
  restartActiveCrawlStatusStreams: () => void;
};

export function useCrawlListActions({
  databaseId,
  crawlsQuery,
  schedulesQuery,
  crawlScheduleByJobId,
  clearLiveStatusForCrawl,
  restartCrawlStatusStreamIfStopped,
  restartActiveCrawlStatusStreams,
}: UseCrawlListActionsOptions) {
  const { translate } = useI18n();
  const queryClient = useQueryClient();

  const deleteCrawlMutation = useDeleteKnowledgeCrawlMutation();
  const deleteCrawlScheduleMutation = useDeleteKnowledgeCrawlScheduleMutation();
  const restartCrawlMutation = useRestartKnowledgeCrawlMutation();
  const retryFailedCrawlMutation = useRetryFailedKnowledgeCrawlMutation();
  const resumeAbandonedCrawlMutation =
    useResumeAbandonedKnowledgeCrawlMutation();

  const pendingDelete = useKnowledgeDatabaseSharedStore(
    (state) => state.pendingDelete,
  );
  const setPendingDelete = useKnowledgeDatabaseSharedStore(
    (state) => state.setPendingDelete,
  );

  const handleManualRefresh = () => {
    void crawlsQuery.refetch();
    void schedulesQuery.refetch();
    invalidateKnowledgeDatabaseSummaries(queryClient, databaseId);
    // List refetch does not include live histograms; reconnect streams for a fresh snapshot.
    restartActiveCrawlStatusStreams();
  };

  const handleRestartCrawl = (crawl: WithResourceId<CrawlJob>) => {
    restartCrawlMutation.mutate(
      {
        databaseId,
        crawlId: crawl.id,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.crawlRestartSuccessTitle'),
            description: translate('knowledge.crawlRestartSuccessDescription'),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlRestartError'),
          );
        },
      },
    );
  };

  const handleRetryFailedCrawl = (crawl: WithResourceId<CrawlJob>) => {
    const schedule = crawlScheduleByJobId.get(crawl.id);
    retryFailedCrawlMutation.mutate(
      {
        databaseId,
        crawl,
        scheduleId: schedule?.id,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.crawlRetrySuccessTitle'),
            description: translate('knowledge.crawlRetrySuccessDescription'),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlRetryError'),
          );
        },
      },
    );
  };

  const handleResumeAbandonedCrawl = (crawl: WithResourceId<CrawlJob>) => {
    resumeAbandonedCrawlMutation.mutate(
      {
        databaseId,
        crawlId: crawl.id,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.crawlResumeSuccessTitle'),
            description: translate('knowledge.crawlResumeSuccessDescription'),
          });
          clearLiveStatusForCrawl(crawl.id);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlResumeError'),
          );
        },
      },
    );
  };

  const handleRequestDeleteCrawl = (crawl: WithResourceId<CrawlJob>) => {
    const schedule = crawlScheduleByJobId.get(crawl.id);

    setPendingDelete({
      type: 'crawl',
      id: crawl.id,
      label: crawl.startUrl ?? crawl.id,
      documentCount: crawl.selectedUrlCount ?? 0,
      scheduleId: schedule?.id,
    });
  };

  const confirmDeleteCrawl = () => {
    if (!pendingDelete || pendingDelete.type !== 'crawl') {
      return;
    }

    const deletion = pendingDelete;

    const deleteCrawl = () => {
      deleteCrawlMutation.mutate(
        {
          databaseId,
          crawlId: deletion.id,
        },
        {
          onSuccess: () => {
            toast.success({
              title: translate('knowledge.deleteSuccessTitle'),
              description: translate(
                'knowledge.deleteCrawlSuccessDescription',
                {
                  url: deletion.label ?? deletion.id,
                },
              ),
            });
          },
          onError: (error) => {
            void toastMutationError(
              error,
              translate('knowledge.deleteErrorTitle'),
              translate('knowledge.deleteErrorDescription'),
            );
          },
          onSettled: () => {
            setPendingDelete(null);
          },
        },
      );
    };

    if (deletion.scheduleId) {
      deleteCrawlScheduleMutation.mutate(
        {
          databaseId,
          scheduleId: deletion.scheduleId,
        },
        {
          onSuccess: () => {
            deleteCrawl();
          },
          onError: (error) => {
            void toastMutationError(
              error,
              translate('knowledge.deleteErrorTitle'),
              translate('knowledge.deleteCrawlScheduleCleanupErrorDescription'),
            );
          },
        },
      );
      return;
    }

    deleteCrawl();
  };

  const handleCrawlContinuedAfterSelection = (crawlId: string) => {
    clearLiveStatusForCrawl(crawlId);
    restartCrawlStatusStreamIfStopped(crawlId);
  };

  return {
    handleManualRefresh,
    handleRestartCrawl,
    handleRetryFailedCrawl,
    handleResumeAbandonedCrawl,
    handleRequestDeleteCrawl,
    pendingDelete,
    setPendingDelete,
    confirmDeleteCrawl,
    isDeletingCrawl:
      deleteCrawlMutation.isPending || deleteCrawlScheduleMutation.isPending,
    handleCrawlContinuedAfterSelection,
  };
}
